import React from 'react';
import { useParams } from 'react-router';
import { TextVariant } from '@naf/text';
import { Grid, GridCol, GridRow } from '@naf/grid';
import StA from '@styles/articles/StyledArticle';
import { NotFound } from '@loadable-elements/NotFound';
import { useWindowLocation } from '../../../../hooks/Window/useWindowLocation';
import useSelector from '../../../../redux/typedHooks';
import { useLocationDocument } from '../../useLocationDocument';
import { LoaderContent } from '../../../LoaderPage';
import SchemaArticle from '../../../../components/structured-data/SchemaArticle/SchemaArticle';
import { useSendGTMEventOnce } from '../../../../hooks/GTM/useSendGTMEventOnce';
import BreadCrumb from '../../../../components/breadcrumb/BreadCrumb';
import BlockContent from '../../../../components/block-content/BlockContent';
import FeedbackSurvey from '../../../../components/feedbacksurvey/FeedbackSurvey';
import { ArticleType } from '../../../../../../types/articleType';
import { MetaData } from '../../../../components/metaData/MetaData';
import { InternalSearchCardType } from '../../../../components/layout/InternalSearchMetaData';
import { LayoutWithMainContent } from '../../../../components/layout/Layout';
import RelatedBenefits from '../RelatedBenefits/RelatedBenefits';
import { useCloudinary } from '../../../../hooks/Cloudinary/useCloudinary';
import * as S from './LocalArticle.styles';

export const LocalArticle = () => {
  const { slug } = useParams<{ slug: string }>();
  const windowPath = useWindowLocation();
  const {
    document: article,
    meta: { isUpdating },
  } = useLocationDocument<ArticleType>(slug);

  const url = useWindowLocation();
  const categoriesState = useSelector((state) => state.sitestructure);
  const mappedCategory = article?.category?.slug
    ? categoriesState?.mappedCategories[article.category.slug]?.data
    : null;
  const { getProcessedImage } = useCloudinary();

  useSendGTMEventOnce(
    article && {
      page_type: 'LocalArticle',
      pageCategory: `${mappedCategory?.prettyUrl}/${article.name}`,
      contentId: article.id,
    },
    [article],
  );

  if (!article && isUpdating) {
    return <LoaderContent />;
  }
  if (!article) {
    return <NotFound />;
  }

  const {
    name,
    ingress,
    meta,
    body,
    video,
    localArticleImage,
    relatedContent,
    seoConfig,
    metaDisplay,
    openGraphImage,
    tags,
  } = article;
  const author = meta?.editor?.name;

  const cldOpenGraphImage = getProcessedImage(openGraphImage?.publicId || openGraphImage?.public_id)?.toURL();

  return (
    <LayoutWithMainContent
      title={name}
      description={ingress}
      isHiddenFromSearch={seoConfig?.isHiddenFromSearch}
      seoDescription={seoConfig?.introduction || undefined}
      seoTitle={seoConfig?.title || undefined}
      imgUrl={localArticleImage?.url}
      internalSearchMetaData={{
        cardType: InternalSearchCardType.LocalArticle,
        imageUrl: localArticleImage?.url,
        tags,
      }}
      url={url}
    >
      <SchemaArticle
        title={name}
        description={ingress}
        imgUrl={cldOpenGraphImage || localArticleImage?.url}
        datePublished={meta?.publishAt}
        dateModified={meta?.updatedAt}
        url={url}
        id={slug}
        author={author}
      />
      <Grid>
        <GridRow>
          <GridCol s="12" m="12" l="12" xl="12">
            <BreadCrumb />
            <GridRow>
              <GridCol s="12" m="12" l="8" xl="8">
                {localArticleImage?.url && !video && (
                  <S.ImageWrapper>
                    <S.ImageFigure>
                      <img src={localArticleImage?.url} alt={localArticleImage?.caption || ''} />
                    </S.ImageFigure>
                  </S.ImageWrapper>
                )}
                {name && (
                  <S.StyledHeaderText tag="h1" variant={TextVariant.Header1}>
                    {name}
                  </S.StyledHeaderText>
                )}
              </GridCol>
            </GridRow>
            <GridRow>
              <GridCol s="12" m="12" l="8" xl="8">
                {ingress && (
                  <>
                    <StA.Ingress tag="p" variant={TextVariant.Ingress}>
                      {ingress}
                    </StA.Ingress>
                    <StA.HRLine />
                  </>
                )}
                {body ? (
                  <StA.Body>
                    <BlockContent value={body} />
                  </StA.Body>
                ) : (
                  <LoaderContent />
                )}
                <MetaData meta={meta} metaDisplay={metaDisplay} />
              </GridCol>
            </GridRow>
          </GridCol>
        </GridRow>
        {relatedContent && relatedContent.length > 0 && <RelatedBenefits relatedContent={relatedContent} />}
        {!metaDisplay?.hideForm && (
          <GridRow>
            <GridCol s="12" m="12" l="8" xl="8">
              <FeedbackSurvey contentUrl={windowPath} />
            </GridCol>
          </GridRow>
        )}
      </Grid>
    </LayoutWithMainContent>
  );
};

export default LocalArticle;
